import { z } from 'zod'

import type {
  CheckPaymentTokenData,
  CreateOrderData,
  CreatePreOrderData,
  FindOrderData,
  GetDeliveryLeadTimeData,
  GetGroupOrderData,
  GetGroupOrderDetail1Data,
  GetOrderData,
  GetOrderItemData,
  GetPaymentInfoV2Data,
  GetPreOrderData,
  GetPreOrderProductDiscountEventData,
  GetPreOrderUserCouponData,
  GetPrintOptionData,
  GetUserCouponData,
  GetUserData,
  GetUserProductDiscountEventData,
  OfflinePaymentData,
} from '~/api-spec/__generated__/pod'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Page, pageSchema } from '~/schema/page'

import {
  type Coupon,
  CouponSchema,
  type DeliveryAddress,
  type DeliveryAddressBook,
  DeliveryAddressBookSchema,
  DeliveryAddressSchema,
  type DeliveryLeadTime,
  DeliveryLeadTimeSchema,
  type DirectOrder,
  DirectOrderSchema,
  type KmmOrder,
  KmmOrderSchema,
  type OrderItem,
  OrderItemSchema,
  type ParentOrder,
  type ParentOrderDetail,
  ParentOrderDetailSchema,
  ParentOrderSchema,
  type ParentPayment,
  ParentPaymentSchema,
  type PodUser,
  PodUserSchema,
  type PreOrder,
  type PreOrderProductDiscountEvent,
  PreOrderProductDiscountEventSchema,
  PreOrderSchema,
  type PrintOption,
  PrintOptionSchema,
  type ProductDiscountEvent,
  ProductDiscountEventSchema,
} from './schemas'

const ENTITY = 'PodOrder'

// 생성 타입과 실제 와이어가 어긋나는 필드. 2026-10-06 POD 서버 develop(edfdcec) 코드로 확인:
//   Kotlin nullable 을 키째 null 로 싣는데 스펙이 optional 로만 그리는 필드 —
//     사용자 응답의 delivery* (UserResponse 별칭 타입, user/controller/response/UserResponse.kt:47),
//     주문의 productDiscountEventName·productDiscountRate(OrderResponse.kt),
//     시그니처 주문 항목의 problemPageCount·revision·grade 등(OrderItemResponse.kt:146),
//     KMM 주문(OrderDto.kt:16)의 nullable 전부, Page 봉투의 content·totalPages 등
//   coverData — customData 는 JSON 배열인데 스펙은 object 로 그리고, ObjectNode? 칸과 맞춤 교재의 coverData 를
//     키째 null 로 싣는데 optional 로만 그린다(product/model/type/CoverData.kt:12)
//   deliveryRecipientStudentNames — 서버 List<String>? 에 @Schema(nullable) 를 달아 스펙이 null 을
//     배열 원소에 붙인다((string | null)[]). 실제로는 배열 전체가 null 이다
// 스키마가 실제 와이어를 검증하므로 입력을 이 한 자리에서 unknown 으로 넘긴다.
const asWire = (
  raw:
    | GetUserData
    | GetPreOrderData
    | GetOrderData
    | GetOrderItemData
    | GetGroupOrderDetail1Data
    | GetPaymentInfoV2Data
    | FindOrderData,
): unknown => raw

export const adaptPodUser = (raw: GetUserData): PodUser =>
  parseApiResponse(PodUserSchema, asWire(raw), ENTITY)

export const adaptProductDiscountEvent = (
  raw: GetUserProductDiscountEventData,
): ProductDiscountEvent => parseApiResponse(ProductDiscountEventSchema, raw, ENTITY)

export const adaptPreOrderProductDiscountEvent = (
  raw: GetPreOrderProductDiscountEventData,
): PreOrderProductDiscountEvent => parseApiResponse(PreOrderProductDiscountEventSchema, raw, ENTITY)

export const adaptCoupons = (raw: GetUserCouponData | GetPreOrderUserCouponData): Coupon[] =>
  parseApiResponse(CouponSchema.array(), raw, ENTITY)

export const adaptDeliveryLeadTime = (raw: GetDeliveryLeadTimeData): DeliveryLeadTime =>
  parseApiResponse(DeliveryLeadTimeSchema, raw, ENTITY)

// 주소록 응답은 커밋된 pod 스펙 스냅샷에 없어 생성 타입이 없다. 입력은 unknown 으로 받고 스키마가 검증한다.
export const adaptDeliveryAddressBook = (raw: unknown): DeliveryAddressBook =>
  parseApiResponse(DeliveryAddressBookSchema, raw, ENTITY)

export const adaptDeliveryAddress = (raw: unknown): DeliveryAddress =>
  parseApiResponse(DeliveryAddressSchema, raw, ENTITY)

export const adaptPreOrder = (raw: GetPreOrderData): PreOrder =>
  parseApiResponse(PreOrderSchema, asWire(raw), ENTITY)

const _PreOrderCreationSchema = z.object({ id: PreOrderSchema.shape.id })

export const adaptPreOrderCreation = (raw: CreatePreOrderData): PreOrder['id'] =>
  parseApiResponse(_PreOrderCreationSchema, raw, ENTITY).id

export const adaptDirectOrders = (raw: GetOrderData): Page<DirectOrder> =>
  parseApiResponse(pageSchema(DirectOrderSchema), asWire(raw), ENTITY)

export const adaptOrderItems = (raw: GetOrderItemData): OrderItem[] =>
  parseApiResponse(OrderItemSchema.array(), asWire(raw), ENTITY)

export const adaptPrintOption = (raw: GetPrintOptionData): PrintOption =>
  parseApiResponse(PrintOptionSchema, raw, ENTITY)

export const adaptParentOrders = (raw: GetGroupOrderData): Page<ParentOrder> =>
  parseApiResponse(pageSchema(ParentOrderSchema), raw, ENTITY)

export const adaptParentOrderDetail = (raw: GetGroupOrderDetail1Data): ParentOrderDetail =>
  parseApiResponse(ParentOrderDetailSchema, asWire(raw), ENTITY)

export const adaptParentPayment = (raw: GetPaymentInfoV2Data): ParentPayment =>
  parseApiResponse(ParentPaymentSchema, asWire(raw), ENTITY)

export const adaptParentPaymentTokenValidity = (raw: CheckPaymentTokenData): boolean =>
  parseApiResponse(z.boolean(), raw, ENTITY)

// 현장 결제로 바꾼 주문의 결제 토큰. 서버가 JSON 이 아닌 text/plain 으로 보낸다
export const adaptParentOrderPaymentToken = (raw: OfflinePaymentData): string =>
  parseApiResponse(z.string(), raw, ENTITY)

const _OrderCreationSchema = z.object({ orderId: KmmOrderSchema.shape.id })

export const adaptKmmOrderCreation = (raw: CreateOrderData): KmmOrder['id'] =>
  parseApiResponse(_OrderCreationSchema, raw, ENTITY).orderId

export const adaptKmmOrders = (raw: FindOrderData): KmmOrder[] =>
  parseApiResponse(KmmOrderSchema.array(), asWire(raw), ENTITY)
